"use client";

/**
 * Analytics for the Support Hub Typesense search modal.
 *
 * Implements the Analytics team spec for:
 * - `view_search_results` (GA4) - debounced live search input
 * - `view_promotion` (Enhanced eCommerce) - suggestion impressions in the overlay
 * - `select_promotion` (Enhanced eCommerce) - suggestion clicks and "see all" paths
 *
 * Events are sent via `dataLayer.push` helpers in `src/helpers/analytics.ts`.
 */
import type { CrhQuestionDocument } from "../../../actions/typesenseSearch";
import {
  type AnalyticsPromotion,
  analyticsSelectPromotion,
  analyticsViewPromotion,
  analyticsViewSearchResults,
} from "../../../helpers/analytics";
import type { AutocompleteState } from "@algolia/autocomplete-core";
import { useEffect, useRef } from "react";

/** GA4 / module-tracking parameter values for the CRH search overlay. */
const CRH_CREATIVE_NAME = "crh questions";
const CRH_PROMOTION_MODULE = "crh-search-results_standard";
const CRH_LIVE_SEARCH_CONTENT_TYPE = "crh live search";

const CRH_SEARCH_SEE_ALL_ID = "see-all-results";
const CRH_SEARCH_SEE_ALL_CREATIVE = "crh search";

/**
 * Delay before firing live-search events (`view_search_results`,
 * `view_promotion`) after the user stops typing, so intermediate keystrokes
 * don't each count as a search or an impression.
 */
const SEARCH_DEBOUNCE_MS = 1000;

/** Minimal shape needed from autocomplete items for promotion tracking. */
export type CrhSearchSuggestion = {
  document: CrhQuestionDocument;
};

/** Prefer the short summary title when available for analytics labels. */
export function questionTitleFromDocument(doc: CrhQuestionDocument): string {
  return doc.summary_question || doc.question;
}

/** Map visible suggestions to Enhanced eCommerce promotion objects. */
function promotionsFromSuggestions(
  suggestions: CrhSearchSuggestion[],
): AnalyticsPromotion[] {
  return suggestions.map((suggestion, index) => ({
    id: suggestion.document.id,
    name: questionTitleFromDocument(suggestion.document),
    creative: CRH_CREATIVE_NAME,
    position: index + 1,
  }));
}

/** Flatten all autocomplete collections into a single suggestion list. */
function flatSuggestions<T extends CrhSearchSuggestion>(
  collections: AutocompleteState<T>["collections"],
): T[] {
  return collections.flatMap((collection) => collection.items);
}

/**
 * Track `select_promotion` when the user submits the search form (Enter) or
 * clicks "See all results" - i.e. they navigate to the full results page
 * rather than picking a single suggestion.
 */
export function trackCrhSearchSeeAllClick(
  query: string,
  suggestions: CrhSearchSuggestion[],
) {
  analyticsSelectPromotion({
    promotionModule: CRH_PROMOTION_MODULE,
    promotions: [
      {
        id: CRH_SEARCH_SEE_ALL_ID,
        name: query,
        creative: CRH_SEARCH_SEE_ALL_CREATIVE,
        position: suggestions.length + 1,
      },
    ],
    availableElements: Math.max(suggestions.length, 1),
  });
}

/**
 * Track `select_promotion` when the user clicks or keyboard-selects a single
 * Q&A suggestion from the overlay.
 */
export function trackCrhSearchSuggestionClick(
  item: CrhSearchSuggestion,
  suggestions: CrhSearchSuggestion[],
) {
  const position =
    suggestions.findIndex(
      (candidate) => candidate.document.id === item.document.id,
    ) + 1;

  analyticsSelectPromotion({
    promotionModule: CRH_PROMOTION_MODULE,
    promotions: [
      {
        id: item.document.id,
        name: questionTitleFromDocument(item.document),
        creative: CRH_CREATIVE_NAME,
        position,
      },
    ],
    availableElements: suggestions.length,
  });
}

/**
 * Hook for passive analytics tied to autocomplete state changes.
 * Call once from `TypesenseSearchModal`; click events are tracked separately
 * via `trackCrhSearchSeeAllClick` / `trackCrhSearchSuggestionClick`.
 */
export function useTypesenseSearchAnalytics<T extends CrhSearchSuggestion>({
  isOpen,
  autocompleteState,
  minQueryLength,
}: {
  isOpen: boolean;
  autocompleteState: AutocompleteState<T>;
  /** Must match the modal's search threshold so analytics align with live search. */
  minQueryLength: number;
}) {
  /**
   * Last result set reported via `view_promotion`, so slowly typing a query
   * that keeps returning the same questions only counts as one impression.
   */
  const lastPromotionImpressionKeyRef = useRef<string | null>(null);

  // GA4 `view_search_results` — fire 1s after the user stops typing.
  useEffect(() => {
    if (!isOpen) return;

    const query = autocompleteState.query.trim();
    if (query.length < minQueryLength) return;

    const timer = window.setTimeout(() => {
      analyticsViewSearchResults({
        value: query,
        contentType: CRH_LIVE_SEARCH_CONTENT_TYPE,
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [autocompleteState.query, isOpen, minQueryLength]);

  // Enhanced eCommerce `view_promotion` - suggestion impressions in the
  // overlay, fired 1s after the results settle.
  useEffect(() => {
    if (!isOpen) return;

    const query = autocompleteState.query.trim();
    if (query.length < minQueryLength) {
      // Emptying the input starts a new search, so the next results are
      // tracked even if they match what was last sent.
      lastPromotionImpressionKeyRef.current = null;
      return;
    }

    if (!autocompleteState.isOpen) return;
    if (autocompleteState.status === "loading") return;

    const suggestions = flatSuggestions(autocompleteState.collections);
    if (suggestions.length === 0) return;

    const impressionKey = suggestions.map((item) => item.document.id).join(",");
    if (lastPromotionImpressionKeyRef.current === impressionKey) return;

    const timer = window.setTimeout(() => {
      lastPromotionImpressionKeyRef.current = impressionKey;

      analyticsViewPromotion({
        promotions: promotionsFromSuggestions(suggestions),
        availableElements: suggestions.length,
        promotionModule: CRH_PROMOTION_MODULE,
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [
    autocompleteState.collections,
    autocompleteState.isOpen,
    autocompleteState.query,
    autocompleteState.status,
    isOpen,
    minQueryLength,
  ]);

  // Reset impression deduplication when the modal closes so reopening re-tracks.
  useEffect(() => {
    if (!isOpen) {
      lastPromotionImpressionKeyRef.current = null;
    }
  }, [isOpen]);
}
